import { Stack } from '@cmg/design-system';
import defaultTo from 'lodash/defaultTo';
import React from 'react';

import useCheckPassiveOrderBookAccess from '../../../../../../../../common/util/check-access/useCheckInstitutionalPassiveOrderBookAccess';
import {
  IndicationStatus as IndicationStatusType,
  InstitutionalIndicationOrderType,
} from '../../../../../../../../graphql';
import { useIndicationActivitySalesAndTradingContext } from '../../../../context/IndicationActivitySalesAndTradingContext';
import IndicationAcknowledgementBannerComponent from '../../common/indication-acknowledgement-banner/IndicationAcknowledgementBanner';
import IndicationSectionPassedReadContent from '../../common/indication-section-passed-read-content/IndicationSectionPassedReadContent';
import IndicationSectionReadContent from '../../common/indication-section-read-content/IndicationSectionReadContent';
import { IndicationSection } from '../../common/indication-section-read-view/IndicationSection';
import ReadButtons from '../../common/indication-section-read-view/ReadButtons';
import IndicationStatus from '../../common/indication-status/IndicationStatus';
import { useCoveredIndicationAcknowledgementBanner } from './hooks/useCoveredIndicationAcknowledgementBanner';
import { useCreateSalesAndTradingDuplicateReadButtonProps } from './hooks/useCreateSalesAndTradingDuplicateReadButtonProps';
import useCreateSalesAndTradingReadButtonProps from './hooks/useCreateSalesAndTradingReadButtonProps';

export type Props = Readonly<{
  onStartEditing: () => void;
  isIndicationEditable: boolean;
  displayMissingContacts: boolean;
  startEditingInvestorContacts: () => void;
}>;

const EMPTY_ARRAY = [];
const IndicationSectionReadView: React.FC<Props> = ({
  onStartEditing,
  isIndicationEditable,
  displayMissingContacts,
  startEditingInvestorContacts,
}) => {
  const {
    offeringId,
    complianceWarning: { hasComplianceWarning, canByPassComplianceWarning },
    coveredIndication,
    basicPublishedOffering,
    orderBookConfiguration,
    publishedOffering,
  } = useIndicationActivitySalesAndTradingContext();
  const offering = basicPublishedOffering!;
  const indication = coveredIndication!;
  const { canManage: canManagePassiveOrderBook } = useCheckPassiveOrderBookAccess({ offeringId });

  const indicationAcknowledgementBannerProps = useCoveredIndicationAcknowledgementBanner();

  const { readButtonDialogs, ...readButtonProps } = useCreateSalesAndTradingReadButtonProps({
    isIndicationEditable,
    onStartEditing,
  });

  const { duplicateReadButtonDialogs, ...duplicateReadButtonProps } =
    useCreateSalesAndTradingDuplicateReadButtonProps();

  const isDuplicate = indication.status === IndicationStatusType.Duplicate;

  return (
    <IndicationSection
      sectionProps={{
        titleItems: <IndicationStatus status={indication.status} />,
        actions: isDuplicate
          ? [<ReadButtons {...duplicateReadButtonProps} key="duplicate-read-buttons" />]
          : [<ReadButtons {...readButtonProps} key="read-buttons" />],
      }}
    >
      <Stack gap={1}>
        {(canByPassComplianceWarning || !hasComplianceWarning) &&
          isIndicationEditable &&
          !canManagePassiveOrderBook && (
            <IndicationAcknowledgementBannerComponent
              {...indicationAcknowledgementBannerProps}
              managers={offering.syndicate.managers}
              startEditingInvestorContacts={startEditingInvestorContacts}
              acknowledgeButtonDisabledNoContacts={displayMissingContacts}
            />
          )}

        {indication.type === InstitutionalIndicationOrderType.Pass && (
          <IndicationSectionPassedReadContent
            submittedOn={indication.auditInfo.createdAt}
            firmName={
              indication.investorInformation.bankInvestorName ??
              indication.investorInformation.cmgEntityName
            }
          />
        )}
        {indication.type !== InstitutionalIndicationOrderType.Pass && (
          <IndicationSectionReadContent
            filings={defaultTo(publishedOffering?.terms.filings, null)}
            offeringType={defaultTo(basicPublishedOffering?.type, null)}
            securityType={defaultTo(publishedOffering?.security.type, null)}
            issuerName={defaultTo(publishedOffering?.issuer.name, null)}
            submittedOn={indication.auditInfo.createdAt}
            indicationTrancheId={indication.trancheId}
            indicationInstrumentId={indication.instrumentId}
            demandCurrencyCode={indication.currencyCode}
            acknowledgements={indication.institutionalIndicationAcknowledgements}
            indicationVersion={indication.version}
            bndAgentCmgEntityKey={indication.billingAndDeliveryAgentCmgEntityKey}
            interestLevels={indication.interestLevels}
            tranches={defaultTo(offering.orderEntryProfile?.tranches, EMPTY_ARRAY)}
            demandInstruments={defaultTo(publishedOffering?.instruments, null)}
            managers={offering.syndicate.managers}
            securityTypeDisplayName={offering.securityTypeDisplayName!}
            isIndicationPriceAsPercentageParEnabled={
              orderBookConfiguration.isIndicationPriceAsPercentageParEnabled
            }
          />
        )}

        {...readButtonDialogs}
        {...duplicateReadButtonDialogs}
      </Stack>
    </IndicationSection>
  );
};

export default IndicationSectionReadView;
